import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Bird, 
  PawPrint, 
  Bug,
  TrendingUp,
  Calendar,
  Stethoscope,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function StatsCards({ stats }) {
  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState(null);

  const toggleExpand = (cardId) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  const cards = [
    {
      id: 'total',
      title: 'Total de Animais',
      value: stats?.total || 0,
      icon: PawPrint,
      color: 'from-emerald-500 to-teal-600',
      bgLight: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      expandable: true,
      details: [
        { label: 'Aves', value: stats?.byGroup?.AVE || 0, icon: Bird, color: 'text-blue-600' },
        { label: 'Mamíferos', value: stats?.byGroup?.MAM || 0, icon: PawPrint, color: 'text-amber-600' },
        { label: 'Répteis', value: stats?.byGroup?.REPT || 0, icon: Bug, color: 'text-green-600' },
      ]
    },
    {
      id: 'month',
      title: 'Admissões do Mês',
      value: stats?.thisMonth || 0,
      icon: Calendar,
      color: 'from-blue-500 to-indigo-600',
      bgLight: 'bg-blue-50',
      textColor: 'text-blue-700',
      expandable: true,
      trend: stats?.monthTrend,
      details: [
        { label: 'Esta semana', value: stats?.thisWeek || 0 },
        { label: 'Semana passada', value: stats?.lastWeek || 0 },
        { label: 'Mês anterior', value: stats?.lastMonth || 0 },
      ]
    },
    {
      id: 'treatment',
      title: 'Em Tratamento',
      value: stats?.inTreatment || 0,
      icon: Stethoscope,
      color: 'from-purple-500 to-pink-600',
      bgLight: 'bg-purple-50',
      textColor: 'text-purple-700',
      expandable: true,
      details: [
        { label: 'Internação', value: stats?.byLocation?.internacao || 0 },
        { label: 'UTI', value: stats?.byLocation?.uti || 0 },
        { label: 'Quarentena', value: stats?.byLocation?.quarentena || 0 },
        { label: 'Outros', value: stats?.byLocation?.outros || 0 },
      ]
    },
    {
      id: 'released',
      title: 'Alta/Soltura',
      value: stats?.released || 0,
      icon: CheckCircle,
      color: 'from-green-500 to-emerald-600',
      bgLight: 'bg-green-50',
      textColor: 'text-green-700',
      expandable: false,
    },
    {
      id: 'deaths',
      title: 'Óbitos',
      value: stats?.deaths || 0,
      icon: XCircle,
      color: 'from-gray-500 to-gray-600',
      bgLight: 'bg-gray-50',
      textColor: 'text-gray-700',
      expandable: true,
      details: [
        { label: 'Entraram vivos', value: stats?.deathsBreakdown?.afterAdmission || 0 },
        { label: 'Já entraram em óbito', value: stats?.deathsBreakdown?.admittedDead || 0 },
      ]
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="stats-cards">
      {cards.map((card) => {
        const Icon = card.icon;
        const isExpanded = expandedCard === card.id;
        
        return (
          <Card 
            key={card.id}
            className={`relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg ${
              isExpanded ? 'col-span-2 md:col-span-1 row-span-2' : ''
            } ${card.expandable ? 'hover:scale-[1.02]' : ''}`}
            onClick={() => card.expandable && toggleExpand(card.id)}
            data-testid={`stat-card-${card.id}`}
          >
            {/* Gradient header */}
            <div className={`h-2 bg-gradient-to-r ${card.color}`} />
            
            <CardContent className="pt-4 pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {card.title}
                  </p>
                  <p className={`text-3xl font-bold mt-1 ${card.textColor}`}>
                    {card.value}
                  </p>
                  
                  {/* Trend indicator */}
                  {card.trend !== undefined && (
                    <div className={`flex items-center gap-1 mt-1 text-xs ${
                      card.trend >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      <TrendingUp className={`w-3 h-3 ${card.trend < 0 ? 'rotate-180' : ''}`} />
                      <span>{Math.abs(card.trend)}% vs mês anterior</span>
                    </div>
                  )}
                </div>
                
                <div className={`p-2 rounded-lg ${card.bgLight}`}>
                  <Icon className={`w-5 h-5 ${card.textColor}`} />
                </div>
              </div>
              
              {/* Expandable indicator */}
              {card.expandable && (
                <div className="flex justify-center mt-2">
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              )}
              
              {/* Expanded details */}
              {isExpanded && card.details && (
                <div className="mt-4 pt-4 border-t space-y-2 animate-in slide-in-from-top-2 duration-200">
                  {card.details.map((detail, idx) => {
                    const DetailIcon = detail.icon;
                    return (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          {DetailIcon && <DetailIcon className={`w-4 h-4 ${detail.color || 'text-gray-500'}`} />}
                          <span className="text-gray-600">{detail.label}</span>
                        </div>
                        <Badge variant="outline" className={card.textColor}>
                          {detail.value}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
